import { useState } from "react";
import { toast } from "react-toastify";
import { useAdminAccountControllerDeleteAccount } from "service";
import { AdminAccountResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";

export interface UseDeleteMember {
  pendingMember: AdminAccountResDto | null;
  isSubmitting: boolean;
  requestDelete: (member: AdminAccountResDto) => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
}

const useDeleteMember = (): UseDeleteMember => {
  const [pendingMember, setPendingMember] = useState<AdminAccountResDto | null>(
    null,
  );
  // IDs with a delete currently in flight. `isLoading` on the mutation itself
  // is shared across every member, so it can't tell "a request is running"
  // from "a request is running for the member whose dialog happens to be
  // open right now" — this does, and also lets the dialog auto-close only
  // when its own member's request is the one that resolved.
  const [submittingMemberIds, setSubmittingMemberIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate } = useAdminAccountControllerDeleteAccount({
    onSuccess: (_data, variables) => {
      toast.success("Member deleted successfully.");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/accounts",
          operationId: "adminAccountControllerGetAll",
          variables: {},
        }),
      );
      const deletedId = variables.pathParams.id;
      setPendingMember((current) =>
        current?.id === deletedId ? null : current,
      );
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(deletedId);
        return next;
      });
    },
    onError: (_error, variables) => {
      toast.error("Couldn't delete the member. Please try again.");
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(variables.pathParams.id);
        return next;
      });
    },
  });

  const requestDelete = (member: AdminAccountResDto) =>
    setPendingMember(member);
  const cancelDelete = () => setPendingMember(null);
  const confirmDelete = () => {
    if (!pendingMember) return;
    setSubmittingMemberIds((current) => new Set(current).add(pendingMember.id));
    mutate({ pathParams: { id: pendingMember.id } });
  };

  return {
    pendingMember,
    isSubmitting: pendingMember
      ? submittingMemberIds.has(pendingMember.id)
      : false,
    requestDelete,
    confirmDelete,
    cancelDelete,
  };
};

export default useDeleteMember;
